import type { AsidePanelKey, MainPanelKey, PanelsStore } from "@/panels/modules/panels/store/panels.store.types";

import { usePanelsStore } from "@/panels/modules/panels/store/panels.store";

// ─── Value selector hooks ─────────────────────────────────────────────────────
// Each returns a stored ref or primitive. The store's setters replace only the
// changed slice, so default Object.is comparison is correct and `useShallow` is
// unnecessary. Prefer these over `usePanelsStore(...)` so a component only
// re-renders for the slice it actually reads.

/** Subscribe to a main panel's full config. Re-renders when any field on that panel changes. */
export const useMainPanelConfig = (panelId: MainPanelKey) => usePanelsStore((state) => state[panelId]);

/** Subscribe to an aside panel's full config (sizes, collapsed state). */
export const useAsidePanelConfig = (panelId: AsidePanelKey) => usePanelsStore((state) => state[panelId]);

/** Subscribe to the topbar's full config (heights, collapsed state). */
export const useTopbarConfig = () => usePanelsStore((state) => state.topbar);

/** Subscribe to the persisted left-vs-right main-panel split percentage. */
export const useLeftMainPanelDefaultSize = () => usePanelsStore((state) => state.leftMainPanelDefaultSize);

// ─── Action helpers ───────────────────────────────────────────────────────────
// Actions are stable refs; these read them from getState() so a caller never opens
// a subscription just to dispatch. Safe in components, effects, and non-React modules.

/** Curried: `setSelectedTab(panelId)(tab)`. Updates a main panel's selected tab. */
export const setSelectedTab = (...args: Parameters<PanelsStore["setSelectedTab"]>) =>
    usePanelsStore.getState().setSelectedTab(...args);

/** Curried: `setIsClosed(panelId)(tab)(isClosed)`. Switches to the other tab when closing the active one. */
export const setIsClosed = (...args: Parameters<PanelsStore["setIsClosed"]>) =>
    usePanelsStore.getState().setIsClosed(...args);

/** Curried: `setIsMinimized(panelId)(tab)(isMinimized)`. Mirror-writes the paired tab when applicable. */
export const setIsMinimized = (...args: Parameters<PanelsStore["setIsMinimized"]>) =>
    usePanelsStore.getState().setIsMinimized(...args);

/** Set an aside panel's config (left/right sidebar sizes, collapsed state). */
export const setAsidePanelConfig = (...args: Parameters<PanelsStore["setAsidePanelConfig"]>) =>
    usePanelsStore.getState().setAsidePanelConfig(...args);

/** Set the topbar's config (heights, collapsed state). */
export const setTopbarConfig = (...args: Parameters<PanelsStore["setTopbarConfig"]>) =>
    usePanelsStore.getState().setTopbarConfig(...args);

/** Set a main panel's config (selectedTab plus tab configs). */
export const setMainPanelConfig = (...args: Parameters<PanelsStore["setMainPanelConfig"]>) =>
    usePanelsStore.getState().setMainPanelConfig(...args);

/** Set the left-vs-right main-panel split percentage. */
export const setLeftMainPanelDefaultSize = (...args: Parameters<PanelsStore["setLeftMainPanelDefaultSize"]>) =>
    usePanelsStore.getState().setLeftMainPanelDefaultSize(...args);

/** Select the "Flows" tab on every main panel that has one. */
export const selectFlowsTab = (...args: Parameters<PanelsStore["selectFlowsTab"]>) =>
    usePanelsStore.getState().selectFlowsTab(...args);
